import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { NotificationSoundKey } from '@magimp/shared';
import { useToastStore } from '../../../stores/toast.js';

const AUDIO_BASE_PATH = '/audio/notifications/';

/**
 * @aiPurpose Settings preview helper (#1069). Returns a `previewSound`
 *   callback that plays one mp3 in response to a real user gesture
 *   (button click); reuses a single `HTMLAudioElement` so a rapid
 *   second click cancels the prior playback rather than stacking.
 * @aiInvariant audio MUST be triggered from a user-event handler so
 *   the browser's autoplay policy permits playback. The hook never
 *   calls `.play()` outside a synchronous click frame, and the .play
 *   promise rejection (autoplay-block, decode-error, file-missing) is
 *   funnelled into a non-blocking toast — it never throws into React.
 */
export function useNotificationSoundPreviewer(): {
  previewSound: (soundKey: NotificationSoundKey) => void;
} {
  const { t } = useTranslation('settings');
  const showToast = useToastStore((s) => s.show);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      const el = audioRef.current;
      if (el) {
        el.pause();
        el.src = '';
        audioRef.current = null;
      }
    };
  }, []);

  const previewSound = useCallback(
    (soundKey: NotificationSoundKey) => {
      try {
        let el = audioRef.current;
        if (!el) {
          el = new Audio();
          audioRef.current = el;
        }
        el.pause();
        el.currentTime = 0;
        el.src = `${AUDIO_BASE_PATH}${soundKey}`;
        // INVARIANT (#1069): the .play() return value is a Promise on
        //   modern browsers and rejects when the user gesture is not
        //   honoured or when the file fails to decode. Catch into a
        //   toast; do NOT re-throw.
        const playback = el.play();
        if (playback && typeof playback.catch === 'function') {
          playback.catch(() => {
            showToast({ tone: 'error', text: t('notifications.previewFailed') });
          });
        }
      } catch {
        showToast({ tone: 'error', text: t('notifications.previewFailed') });
      }
    },
    [showToast, t],
  );

  return { previewSound };
}
